import React, { useState, useEffect, useRef, useCallback } from 'react';
import T from 'prop-types';
import { includes, map } from 'lodash-es';
import cx from 'classnames';

import { AppsList } from './AppsList';
import Tooltip from '../../../../../components/Tooltip';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import SearchTypeahead from '../../../../../components/SearchTypeahead';
import { isAppsChanged } from '../../utils';
import axios from '../../../../../configs/axios';

const emptyArray = [];
const emptyObject = {};

const redirectClarificationMessage = (
  <div className="redirect-clarification-message">
    <h2 className="redirect-clarification-title">Please Select Additional Apps</h2>
    <p className="redirect-clarification-text">
      We need more information before we can generate your report. Please select 2-4 additional
      apps.
    </p>
  </div>
);

const AppsSelection = ({
  customButton,
  selectionForbidden,
  preSelectedApp,
  defaultSearchResults = emptyArray,
  customTexts = emptyObject,
  enableSmartSearch = false,
  redirectedFlow,

  onChange,
  onSearchFocus,
  onNextStepClick,
  onForbiddenStatusChange,
  coverImage,
}) => {
  const [selectedApps, setSelectedApps] = useState(preSelectedApp || emptyArray);
  const [lockLoading, setLockLoading] = useState(false);
  const searchTypeahead = useRef();

  const searchTypeaheadRef = useCallback(() => searchTypeahead.current.typeaheadInstance(), []);

  useEffect(() => {
    if (isAppsChanged(preSelectedApp, selectedApps)) {
      onChange(selectedApps);
    }
  }, [selectedApps]);

  useEffect(() => {
    setSelectedApps((prevState) => {
      if (isAppsChanged(prevState, preSelectedApp)) return preSelectedApp;
      return prevState;
    });
  }, [preSelectedApp]);

  const handleSmartSearch = (app) => {
    setLockLoading(true);
    axios
      .post('/search/similar_apps', { ref_no: app.ref_no, store: app.store })
      .then(({ data }) => {
        setSelectedApps((prevState) => [...prevState, ...data.results.slice(0, 4)]);
        setLockLoading(false);
      })
      .catch(() => {
        setLockLoading(false);
      });
  };

  const handleChange = useCallback(
    ([app], searchQuery) => {
      if (!includes(map(selectedApps, 'ref_no'), app.ref_no) && selectedApps.length < 5) {
        setSelectedApps((prevState) => {
          const nextSelectedApps = [...prevState, app];
          if (enableSmartSearch && nextSelectedApps.length === 1) {
            handleSmartSearch(app);
          }
          return nextSelectedApps;
        });

        if (searchTypeaheadRef) {
          searchTypeaheadRef().clear();
          searchTypeaheadRef().setState({ text: searchQuery });
        }
      }
    },
    [selectedApps]
  );

  const handleItemSelected = useCallback(
    (app) => ({
      itemSelected: app.dls_30d === 0 || includes(map(selectedApps, 'ref_no'), app.ref_no),
    }),
    [selectedApps]
  );

  const handleAppRemove = useCallback((refNo) => {
    setSelectedApps((prevState) => prevState.filter(({ ref_no }) => ref_no !== refNo));
  }, []);

  const handlePlaceholderClick = useCallback(() => {
    searchTypeaheadRef().focus();
    searchTypeaheadRef().inputNode.select();
  }, [searchTypeaheadRef]);

  const handleSearch = useCallback(
    (results, searchQuery, forbidden) => {
      if (!selectionForbidden && forbidden) onForbiddenStatusChange(forbidden);
    },
    [onForbiddenStatusChange]
  );

  const filterSearchResults = useCallback(
    (results) => results.filter((app) => app.dls_30d !== 0),
    []
  );

  const buttonDisabled = selectedApps.length < 3;
  const hasDefaultSearchResults = defaultSearchResults.length > 0;
  const searchMinLength = hasDefaultSearchResults ? 0 : 1;

  return (
    <div
      className={cx('app-selection-container flex-no-shrink d-flex flex-wrap flex-column w-100', {
        'cover-image': coverImage,
      })}
    >
      {coverImage && (
        <div className="cover-image-box">
          <img src={coverImage} alt="cover mobile" />
        </div>
      )}
      <div className="app-selection-container-wrapper">
        {lockLoading && <LoadingOverlay loading />}
        {redirectedFlow && redirectClarificationMessage}
        <div
          className={cx('report-analysis-search-input', { 'hidden-dropdown': selectionForbidden })}
        >
          <SearchTypeahead
            onChange={handleChange}
            onFocus={onSearchFocus}
            onSearchCallback={handleSearch}
            itemSelected={handleItemSelected}
            decorateItems={filterSearchResults}
            ref={searchTypeahead}
            searchAsset="apps"
            maxHeight="332px"
            autoFocus={false}
            placeholder={customTexts.search_placeholder || 'Search for any app'}
            decorators={['dls_30d', 'big_icon_url']}
            defaultSearchResults={defaultSearchResults}
            defaultSearchResultsHeader={hasDefaultSearchResults ? 'Popular Apps' : ''}
            disabled={selectionForbidden}
            minLength={searchMinLength}
            disableSearchOnFocus
            disableRecentResults
            includesUnified
            clearButton
          />
        </div>

        <AppsList
          apps={selectedApps}
          customTexts={customTexts}
          onAppRemove={handleAppRemove}
          onPlaceholderClick={handlePlaceholderClick}
        />
        <Tooltip content={buttonDisabled ? 'You must select at least 3 apps' : null}>
          {customButton || (
            <button
              type="button"
              onClick={() => onNextStepClick('userReportForm')}
              className={cx('btn report-analysis-next-btn', { disabled: buttonDisabled })}
            >
              {customTexts.button_text || 'Next'}
            </button>
          )}
        </Tooltip>
      </div>
    </div>
  );
};

AppsSelection.propTypes = {
  customButton: T.node,
  preSelectedApp: T.array,
  defaultSearchResults: T.array,
  selectionForbidden: T.bool.isRequired,
  customTexts: T.shape({
    app_selection_text: T.string,
    button_text: T.string,
    search_placeholder: T.string,
  }),
  enableSmartSearch: T.bool,
  redirectedFlow: T.bool,

  onChange: T.func.isRequired,
  onSearchFocus: T.func.isRequired,
  onNextStepClick: T.func.isRequired,
  onForbiddenStatusChange: T.func.isRequired,
  coverImage: T.string,
};

export default AppsSelection;
